<template>
  <div>
    <field-label
      :label="reportTemplates.length > 0 ? 'Modelo de impressão' : ''"
      titleClass="text-body-1"
      titleColorClass="black--text text--lighten-1"
      class="my-4"
    >
      <div class="d-flex gap-8 align-end flex-wrap">
        <select-printing-template v-model="selectedTemplate" type="report" style="max-width: 400px" />
        <v-btn
          @click="resetReport"
          v-if="!!predominantSymptom && !!asasPhaseData"
          color="primary"
          :disabled="isFinishedAttendance"
        >
          Gerar novamente
        </v-btn>
        <v-btn color="primary" :disabled="!report || report.length === 0" :loading="printing" @click="printPdf">
          Imprimir
        </v-btn>
      </div>
    </field-label>

    <field-label label="Relatório" class="mb-4" titleColorClass="grey--text text--darken-2">
      <text-area :readonly="disabled" v-model="report" :rows="20" :disabled="isFinishedAttendance" />
    </field-label>
    <field-label label="Referências" class="mb-4" titleColorClass="grey--text text--darken-2">
      <text-area :readonly="disabled" v-model="references" :disabled="isFinishedAttendance" />
    </field-label>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'

import BasePhase from '@/components/AttendanceForm/general/phase/BasePhase.vue'
import FieldLabel from '@/components/FieldLabel.vue'

const {
  mapGetters: attendanceGettersMap,
  mapActions: attendanceActionsMap,
  mapState: attendanceStateMap,
} = createNamespacedHelpers('attendance')

import {
  ESPONDILOARTRITES_ELEMENTS_OPTIONS,
  IMAGE_EXAM_OPTIONS,
} from '@/components/AttendanceForm/espondiloartrites/constants/asasPhase'
import { SYMPTOM_OPTIONS } from '@/components/AttendanceForm/espondiloartrites/constants/symptom'
import axialAsasMixin from '@/components/AttendanceForm/espondiloartrites/mixins/espondiloartritesAxialAsasMixin'
import perifericAsasMixin from '@/components/AttendanceForm/espondiloartrites/mixins/espondiloartritesPerifericAsasMixin'
import { MEDICINE_CATEGORIES } from '@/constants/medicine'

import DownloadFrame from '@/components/DownloadFrame.vue'
import SelectField from '@/components/SelectField.vue'
import TextArea from '@/components/TextArea.vue'
import { STATUS_FINISHED } from '@/constants/attendance'
import SelectPrintingTemplate from '@/components/PrintingTemplate/SelectPrintingTemplate.vue'
import { PrintingTemplate } from '@/plugins/api/endpoints/printingTemplate'

export default {
  mixins: [axialAsasMixin, perifericAsasMixin],
  extends: BasePhase,
  components: {
    FieldLabel,
    TextArea,
    // SelectField,
    // DownloadFrame,
    SelectPrintingTemplate,
  },
  computed: {
    ...attendanceStateMap(['isServerSyncedData', 'isLocalSyncedData', 'isSavingData', 'attendance']),
    ...attendanceGettersMap(['getPhaseDataByKey']),
    isFinishedAttendance() {
      return this.attendance?.status === STATUS_FINISHED.value
    },
    selectedSymptoms() {
      if (Object.keys(this.symptomsPhaseDataSymptoms).length === 0) {
        return '-'
      }

      return Object.entries(this.symptomsPhaseDataSymptoms)
        .map((e) => ({ key: e[0], data: e[1] }))
        .filter((s) => s.data.value)
        .map((s) =>
          (
            SYMPTOM_OPTIONS.find((o) => o.key === s.key)?.name +
            (s.data?.period.length
              ? ` há ${s.data.period.length} ${
                  this.periodText[s.data.period.type][s.data.period.length > 1 ? 'plural' : 'singular']
                }`
              : '')
          ).firstLetterToLowercase(),
        )
        .join(', ')
    },
    predominantSymptom() {
      return this.getPhaseDataByKey('sintomas')?.predominantSymptom
    },
    computedArticulacoesDolorosasText() {
      return this.articulacoesAcometidasArtrite
        .filter((a) => a.value.dorSelected)
        .map((a) => a.value.label.firstLetterToLowercase())
        .join(', ')
    },
    computedArticulacoesInchadasText() {
      return this.articulacoesAcometidasArtrite
        .filter((a) => a.value.inchSelected)
        .map((a) => a.value.label.firstLetterToLowercase())
        .join(', ')
    },
    computedAreasEntesiteText() {
      return this.areasAcometidasEntesite.map((a) => a.value.label.firstLetterToLowercase()).join(', ')
    },
    computedUnhasDactiliteText() {
      return this.areasAcometidasDactilite
        .filter((a) => a.key.startsWith('unha'))
        .map((a) => a.value.label.replace('Unha - ', '').firstLetterToLowercase())
        .join(', ')
    },
    computedDactilosDactiliteText() {
      return this.areasAcometidasDactilite
        .filter((a) => !a.key.startsWith('unha'))
        .map((a) => a.value.label.replace('Dedo - ', '').firstLetterToLowercase())
        .join(', ')
    },
    computedElementsText() {
      return ESPONDILOARTRITES_ELEMENTS_OPTIONS[this.predominantSymptom]
        ?.filter((el) => this.asasPhaseData[el.key])
        .map((el) => el.name.firstLetterToLowercase())
        .join(', ')
    },
    computedSacroileiteImagemText() {
      if (this.predominantSymptom === 'PERIFERICO' || this.computedSacroileiteImagem != '+') {
        return ''
      }
      const value = IMAGE_EXAM_OPTIONS.find((o) => o.value === this.asasPhaseData.possui_sacroileite_exame_imagem)
      return 'Apresenta ' + (value.label?.firstLetterToLowercase() || value.text.firstLetterToLowercase()) + '\n'
    },
    computedCriteriosAtividadeText() {
      let result = []

      if (this.predominantSymptom === 'AXIAL') {
        if (this.computedBasdai !== '?') result.push('BASDAI ' + this.computedBasdai)
        if (this.computedAsdasPcr !== '?') result.push('ASDAS PCR ' + this.computedAsdasPcr)
        if (this.computedAsdasVhs !== '?') result.push('ASDAS VHS ' + this.computedAsdasVhs)
      }
      if (this.predominantSymptom === 'PERIFERICO') {
        if (this.computedDas28 !== '?') result.push('DAS-28 ' + this.computedDas28)
        if (this.computedDapsa !== '?') result.push('DAPSA ' + this.computedDapsa)
      }
      if (this.computedEva !== '?') result.push('EVA ' + this.computedEva)

      if (this.getPhaseDataByKey('pasi_bsa')?.pasi != null)
        result.push('PASI ' + this.getPhaseDataByKey('pasi_bsa').pasi)
      if (this.getPhaseDataByKey('pasi_bsa')?.bsa != null) result.push('BSA ' + this.getPhaseDataByKey('pasi_bsa').bsa)

      if (result.length === 0) {
        return ''
      }

      return (
        '\nPaciente apresenta os seguintes critérios de atividade de doença associados aos sintomas e sinais inflamatórios descritos acima: ' +
        result.join(', ')
      )
    },
    medicineHistory() {
      return Object.values(this.getPhaseDataByKey('historico_medicamentoso')?.medicines || {}) || []
    },
    previousSistemicMedicinesText() {
      return this.getPreviousMedicineText(MEDICINE_CATEGORIES.SISTEMICOS_TRADICIONAIS)
    },
    previousImunobiologicMedicinesText() {
      return this.getPreviousMedicineText(MEDICINE_CATEGORIES.IMUNOBIOLOGICOS)
    },
    previousAntiinflamatoryMedicinesText() {
      return this.getPreviousMedicineText(MEDICINE_CATEGORIES.ANTI_INFLAMATORIOS)
    },
    medicineHistoryText() {
      if (
        !this.previousSistemicMedicinesText.length &&
        !this.previousImunobiologicMedicinesText.length &&
        !this.previousAntiinflamatoryMedicinesText.length
      ) {
        return ''
      }

      return (
        'Paciente acima já utilizou:\n' +
        (this.previousSistemicMedicinesText.length
          ? `\t- DMARDs e sistêmicos não biológicos: ${this.previousSistemicMedicinesText}\n`
          : '') +
        (this.previousImunobiologicMedicinesText.length
          ? `\t- Imunobiológicos ou pequenas moléculas: ${this.previousImunobiologicMedicinesText}\n`
          : '') +
        (this.previousAntiinflamatoryMedicinesText.length
          ? `\t- Anti-inflamatórios: ${this.previousAntiinflamatoryMedicinesText}\n`
          : '')
      )
    },
    prescriptedMedicinesText() {
      if (!this.getPhaseDataByKey('medicamentos')?.selectedTreatments) {
        return ''
      }
      return this.getPhaseDataByKey('medicamentos')
        .selectedTreatments.map((t) => t.medicine.name)
        .join(', ')
    },
    defaultReport() {
      return (
        `Paciente de ${this.$util.toAge(this.attendance.patient.birthday)} (${this.$util.toFormattedDate(
          this.attendance.patient.birthday,
        )}), em acompanhamento com diagnóstico de espondiloartrite ${
          this.symptomType[this.predominantSymptom]
        } segundo os critérios ASAS 2009 (CID:M07.0).\n\nApresenta os seguintes sintomas: ${this.selectedSymptoms}.${
          this.computedElementsText.length ? ` Além de: ${this.computedElementsText}.` : ''
        }\n` +
        `${
          this.computedArticulacoesDolorosas ||
          this.computedArticulacoesInchadas ||
          this.computedAreasEntesiteText.length
            ? `\nTambém apresenta:\n${
                this.computedArticulacoesDolorosas
                  ? `- dores nas articulações: ${this.computedArticulacoesDolorosasText}\n`
                  : ''
              }${
                this.computedArticulacoesInchadas
                  ? `- inchaço nas articulações: ${this.computedArticulacoesInchadasText}\n`
                  : ''
              }${
                this.computedAreasEntesiteText.length ? `- entesite nas áreas: ${this.computedAreasEntesiteText}\n` : ''
              }${
                this.computedDactilosDactiliteText.length
                  ? `- dactilite nos dáctilos: ${this.computedDactilosDactiliteText}\n`
                  : ''
              }${
                this.computedUnhasDactiliteText.length
                  ? `- alterações ungueais nas unhas: ${this.computedUnhasDactiliteText}\n`
                  : ''
              }`
            : ''
        }` +
        `${this.computedSacroileiteImagemText}${this.computedCriteriosAtividadeText}` +
        `\nTambém não atingiu o mínimo de 5 critérios dos 7 componentes do "Minimal Disease Activity" (MDA) considerados como controle da doença.\n` +
        `\n${this.medicineHistoryText}` +
        `${
          this.medicineHistoryText.length
            ? 'Todos os medicamentos acima foram utilizados em dose plena e de forma regular por no mínimo 3 meses cada, porém o paciente não atingiu remissão ou pelo menos baixa atividade da doença.  Portanto, houve falha terapêutica.'
            : ''
        }\n` +
        `\nO Protocolo Clínico e Diretrizes de Tratamento (PCDT) do Ministério da Saúde para Artrite Psoriásica indica uma sequência de medicamentos a ser instituída caso haja falha na resposta terapêutica à terapia. O primeiro tratamento deve ser realizado com metotrexato, seguido de leflunomida ou sulfassalazina; seguidos de imunobiológicos de 1a linha e então os de 2a linha ou tofacitinibe. Para os casos de artrite axial ou entesite, a 1a etapa contempla os anti-inflamatórios não esteroidais seguidos de imunobiológicos anti-tnf de 1a linha e então os de 2a linha ou tofacitinibe.
http://conitec.gov.br/images/Protocolos/20210531_PCDT_min_artrite_psoriaca.pdf

O Rol da ANS para terapias imunobiológicas para artrite psoriásica prevê a cobertura obrigatória desse tratamento para pacientes com artrite periférica com no mínimo 3 articulações doloridas ou inchadas; ou uma articulação inflamada; ou menos de 5 critérios do MDA; e falha a pelo menos dois medicamentos modificadores do curso de doença sintéticos (MMCDs) por pelo menos 3 meses cada. Para pacientes com artrite axial, a cobertura é para aqueles com BASDAI maior ou igual a 4; ou ASDAS maior que 2,1 ou menos de 5 critérios do MDA e falha à dois AINEs por pelo menos 3 meses.
As terapias imunobiológicas não tem ordem obrigatória para uso, ficando a critério médico a escolha.
https://www.ans.gov.br/images/stories/Plano_de_saude_e_Operadoras/Area_do_consumidor/rol/2021/anexo_ii_dut_2021_rn_4652021.pdf\n` +
        `${
          this.prescriptedMedicinesText
            ? `\nPor isso, considerando o subtipo de doença, sua gravidade, o perfil e histórico de doença da paciente, solicito tratamento com ${this.prescriptedMedicinesText} para uso conforme receita em anexo.
Esse(s) medicamento(s) tem alta resposta ACR em quadros de espondiloartrite como é o caso desse paciente, com eficaz redução da atividade da doença e da progressão radiográfica, além de resposta sustentada a longo prazo e com perfil de segurança adequado comprovados em estudos (segue referência bibliográfica abaixo que contém tais informações). Com esse tratamento, o paciente poderá atingir remissão ou baixa atividade da doença prevenindo e/ou reduzindo a sintomatologia e danos articulares causados pela doença.\n`
            : ''
        }
Comprometo-me a realizar seguimento do caso em consultas regulares para orientar o paciente quanto ao seguimento correto do tratamento, monitorar sua adesão às medidas propostas e observar os efeitos positivos do medicamento assim como tratar com rapidez e responsabilidade os possíveis efeitos adversos associados à terapêutica proposta.

Fico à disposição para eventuais esclarecimentos.`
      )
    },
    defaultReferences() {
      if (!this.getPhaseDataByKey('medicamentos')?.selectedTreatments) {
        return ''
      }
      return this.getPhaseDataByKey('medicamentos')
        .selectedTreatments.map((t) => t.references.map((r) => r.reference).join('\n'))
        .join('\n')
    },
    reportTemplates() {
      return this.templates.map((t) => ({ id: t.id, name: t.name }))
    },
    printReportUrl() {
      return `${this.$util.apiURL()}/attendance/report/print/${this.attendance.id}/${this.selectedReportTemplateId}`
    },
  },
  data() {
    return {
      symptomType: {
        AXIAL: 'axial',
        PERIFERICO: 'periférica',
      },
      report: null,
      references: null,
      resultText: {
        '+': 'positivo',
        '-': 'negativo',
        '?': 'indefinido',
      },
      periodText: {
        MES: {
          singular: 'mês',
          plural: 'meses',
        },
        ANO: {
          singular: 'ano',
          plural: 'anos',
        },
      },
      templates: [],
      /** @type {PrintingTemplate} */
      selectedTemplate: null,
      printing: false,
      selectedReportTemplateId: null,
    }
  },
  async mounted() {
    await this.loadData()
  },
  methods: {
    ...attendanceActionsMap([
      'setAttendanceMedicalReport',
      'setAttendanceMedicalReportReferences',
      'startServerSyncing',
      'endServerSyncing',
    ]),
    async printPdf() {
      await this.saveReport()
      try {
        this.printing = true
        const blob = await this.$api.printingTemplate.print({
          templateId: this.selectedTemplate.id,
          patientId: this.attendance.patient.id,
          text: this.report,
          extraText: this.references,
        })
        this.$util.downloadBlob(blob, 'relatório.pdf')
      } catch (error) {
        this.$root.error(error)
      } finally {
        this.printing = false
      }
    },
    async loadData() {
      this.templates = (await this.$api.getTemplates()).items

      if (this.reportTemplates.length > 0) {
        this.selectedReportTemplateId = this.reportTemplates[0].id
      }
    },
    fillDefaultReportAndReferences() {
      this.report = this.defaultReport
      this.references = this.defaultReferences
    },
    async resetReport() {
      if (await this.$root.confirm('Deseja gerar o relatório novamente? As alterações serão perdidas!')) {
        this.fillDefaultReportAndReferences()
      }
    },
    getDefaultData() {
      return null
    },
    getPreviousMedicineText(type) {
      let previousMedicines = this.medicineHistory.filter((m) => m.medicine.category.id === type)

      if (previousMedicines.length === 0) {
        return ''
      }

      return previousMedicines
        .map((m) => {
          let periods = m.periods
            .map((p) => {
              if (!p.end) {
                return `desde ${this.$util.toFormattedDate(p.start)}`
              }

              return `de ${this.$util.toFormattedDate(p.start)} a ${this.$util.toFormattedDate(p.end)}`
            })
            .join(', ')

          return `${m.medicine.name} (${periods})`
        })
        .join(', ')
    },

    async saveMedicalDocument() {
      const data = {
        report: this.report,
      }

      return this.$api.medicalDocument.create({
        patientId: this.attendance.patient.id,
        kind: 'report',
        data,
        templateId: this.selectedTemplate.id,
        attendanceId: this.attendance.id,
      })
    },
    async saveReport() {
      try {
        await this.saveMedicalDocument()
      } catch (error) {
        console.log(error)
      }

      if (this.isServerSyncedData || this.isSavingData) {
        return
      }

      try {
        await this.startServerSyncing()
        await this.$api.patchAttendance(this.attendance.id, this.attendance)
        await this.endServerSyncing(true)
      } catch (error) {
        await this.endServerSyncing(false)
        if (showError) {
          this.$root.error(error)
        } else {
          this.$logger.warn('Não foi possível salvar os dados do atendimento no servidor!', error)
        }
      }

      return Promise.resolve({})
    },
  },

  watch: {
    active: {
      immediate: true,
      async handler(newVal) {
        if (newVal) {
          this.references = this.attendance.references
          this.report = this.attendance.report

          if (
            (!this.report || this.report.length === 0) &&
            (!this.references || this.references.length === 0) &&
            this.predominantSymptom &&
            this.asasPhaseData
          ) {
            if (
              await this.$root.confirm(
                `O relatório médico está vazio. Deseja preencher com o texto padrão para espondiloartrite ${
                  this.symptomType[this.predominantSymptom]
                }?`,
              )
            ) {
              this.fillDefaultReportAndReferences()
            }
          }
        }
      },
    },
    report(newVal) {
      this.setAttendanceMedicalReport(newVal)
    },
    references(newVal) {
      this.setAttendanceMedicalReportReferences(newVal)
    },
  },
}
</script>

<style scoped>
* :deep(textarea) {
  padding: 10px 20px;
}
</style>
